import {
    BreakMode,
    DRAFT_TIME_SETTINGS,
    MealsMode,
    type Position,
    type TimeEntry,
    TimeEntryType,
    type TimeLogEvent,
    breakModeLabel,
    formatDateShort,
    getSalaryOrDefault,
    parseDurationString,
    parseTimes,
    timeLogActionIcon,
    timeLogActionLabel,
    toDateString,
    toDurationString,
    toTimeString,
} from "@pentacode/core";
import type { DateString, Euros, Meals } from "@pentacode/openapi";
import type { TimeInput } from "@pentacode/ui/components";
import { activeIcon } from "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Textarea } from "../components/text-area/textarea";
import { popover } from "../directives/popover";
import { app, router } from "../init";
import { shared } from "../styles";
import { Checkbox } from "./checkbox";
import { DateInput } from "./date-input";
import "./meals-input";
import type { MealsInput } from "./meals-input";
import "./time-log-event-details";

@customElement("ptc-time-entry-form")
export class TimeEntryForm extends LitElement {
    @property({ attribute: false })
    entry: TimeEntry;

    @property({ attribute: false })
    positions?: Position[];

    @property({ type: Boolean })
    hideMealsAndRevenue = false;

    @property({ type: Boolean })
    hideLogged = false;

    @property({ type: Boolean })
    readonly = false;

    @property({ attribute: false })
    timeLogEvents: TimeLogEvent[] = [];

    @query("ptc-date-input[name='date']")
    private _dateInput: HTMLInputElement;

    @query("ptc-ui-time-input[name='startPlanned']")
    private _startPlannedInput: TimeInput;

    @query("ptc-ui-time-input[name='endPlanned']")
    private _endPlannedInput: TimeInput;

    @query("ptc-ui-time-input[name='startLogged']")
    private _startLoggedInput: TimeInput;

    @query("ptc-ui-time-input[name='endLogged']")
    private _endLoggedInput: TimeInput;

    @query("ptc-ui-time-input[name='startFinal']")
    private _startFinalInput: TimeInput;

    @query("ptc-ui-time-input[name='endFinal']")
    private _endFinalInput: TimeInput;

    @query("ptc-meals-input")
    private _mealsInput: MealsInput;

    @query("input[name='revenue']")
    private _revenueInput: HTMLInputElement;

    @query("ptc-ui-time-input[name='break']")
    private _breakInput: TimeInput;

    @query("ptc-ui-time-input[name='breakPlanned']")
    private _breakPlannedInput: TimeInput;

    @query("ptc-ui-time-input[name='breakLogged']")
    private _breakLoggedInput: TimeInput;

    @query("select[name='position']")
    private _positionSelect: HTMLSelectElement;

    @query("ptc-textarea[name='comment']")
    private _commentInput: Textarea;

    @query("form")
    private _form: HTMLFormElement;

    private get _employee() {
        return this.entry.employeeId ? app.getEmployee(this.entry.employeeId) : null;
    }

    private get _editMode() {
        const { trackingEnabled } = app.getTimeSettings({ timeEntry: this.entry });
        const isPast = this.entry.start < new Date();
        return !trackingEnabled || isPast || !!this.entry.startFinal || !!this.entry.endFinal ? "final" : "planned";
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("entry")) {
            this.updateFields();
        }
    }

    focusField(field: string) {
        if (["start", "end"].includes(field)) {
            const mode = this._editMode as string;
            field = field + mode[0].toUpperCase() + mode.slice(1);
        }
        if (field === "break" && this._editMode === "planned") {
            field = "breakPlanned";
        }
        const el = this.renderRoot!.querySelector(`[name="${field}"]`) as HTMLElement;
        // preventScroll: the docked field is already visible and the roster scrolls the active cell
        // itself, so skip the forced focus reflow on every WASD step.
        el && el.focus({ preventScroll: true });
    }

    updateFields() {
        if (!this.entry) {
            return;
        }
        const position = this.entry.position || this._employee?.positions[0];
        this._positionSelect.value = position?.id.toString() || "";
        this._startPlannedInput.value = toTimeString(this.entry.startPlanned);
        this._endPlannedInput.value = toTimeString(this.entry.endPlanned);
        this._startLoggedInput.value = toTimeString(this.entry.startLogged);
        this._endLoggedInput.value = toTimeString(this.entry.endLogged);
        this._startFinalInput.value = toTimeString(this.entry.startFinal);
        this._endFinalInput.value = toTimeString(this.entry.endFinal);
        this._dateInput.value = this.entry.date;
        this._breakInput.value = typeof this.entry.break === "number" ? toDurationString(this.entry.break) : "";
        this._breakPlannedInput.value =
            typeof this.entry.breakPlanned === "number" ? toDurationString(this.entry.breakPlanned) : "";
        this._breakLoggedInput.value =
            typeof this.entry.breakLogged === "number" ? toDurationString(this.entry.breakLogged) : "";
        this._mealsInput.breakfast = !!this.entry.mealsBreakfast;
        this._mealsInput.lunch = !!this.entry.mealsLunch;
        this._mealsInput.dinner = !!this.entry.mealsDinner;
        this._revenueInput.value = this.entry.revenue.toString();
        this._commentInput.value = this.entry.comment || "";
    }

    private async _updateEntry() {
        const entry = this.entry;
        const formData = new FormData(this._form);

        entry.date = formData.get("date") as DateString;
        entry.comment = formData.get("comment") as string;
        if (entry.type === TimeEntryType.Work) {
            // Read time fields from the elements, not FormData: disabled form-associated
            // elements are skipped on submission, which would wipe the preserved values.
            const [startPlanned, endPlanned] = parseTimes(
                entry.date,
                this._startPlannedInput.value,
                this._endPlannedInput.value
            );
            const [startFinal, endFinal] = parseTimes(
                entry.date,
                this._startFinalInput.value,
                this._endFinalInput.value
            );
            entry.startPlanned = startPlanned;
            entry.endPlanned = endPlanned;
            entry.startFinal = startFinal;
            entry.endFinal = endFinal;
            entry.break = this._breakInput.value ? parseDurationString(this._breakInput.value) : null;
            entry.breakPlanned = this._breakPlannedInput.value
                ? parseDurationString(this._breakPlannedInput.value)
                : null;
            entry.mealsBreakfast = this._mealsInput.breakfast ? (1 as Meals) : (0 as Meals);
            entry.mealsLunch = this._mealsInput.lunch ? (1 as Meals) : (0 as Meals);
            entry.mealsDinner = this._mealsInput.dinner ? (1 as Meals) : (0 as Meals);
            entry.revenue = (formData.get("revenue") ? Number(formData.get("revenue") as string) : 0) as Euros;

            // only update position if there is a positionId set
            const positionIdString = formData.get("position") as string;
            if (positionIdString != null) {
                entry.position = app.getPosition(parseInt(positionIdString))?.position || null;
                entry.positionId = entry.position?.id || null;
            }
        }
    }

    private _validateDate(e: Event) {
        if (!this.entry.employeeId) {
            return;
        }
        const input = e.target as HTMLInputElement;
        const employee = app.getEmployee(this.entry.employeeId);
        const contract = employee && employee.getContractForDate(input.value);
        input.setCustomValidity(!contract ? "Es existiert kein Vertrag für diesen Zeitraum!" : "");
    }

    private async _commitChanges() {
        this.dispatchEvent(new CustomEvent("updateentry", { detail: this.entry, composed: true, bubbles: true }));
    }

    static styles = [
        shared,
        DateInput.styles,
        Textarea.styles,
        Checkbox.styles,
        css`
            :host {
                display: block;
                font-size: inherit;
                color: inherit;
            }

            form {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(15em, 1fr));
            }

            .field > label {
                padding: 0.6em 0.4em;
            }

            .field > label ptc-ui-icon {
                font-size: 0.9em;
                margin: -0.2em 0.2em -0.2em 0;
            }

            input[name="revenue"],
            select {
                font-size: var(--font-size-larger);
                padding: 0.3em 0.5em;
                font-weight: 600;
            }

            ptc-ui-time-input {
                --time-input-opacity-disabled: 1;
                font-weight: 600;
            }

            .dash {
                font-weight: bold;
                margin: 0 0.2em;
            }

            ptc-ui-time-input.break {
                margin-left: 0.2em;
            }

            ptc-ui-time-input.break::part(icon) {
                font-size: 0.8em;
            }

            input[type="number"] {
                text-align: right;
            }

            .time-settings .box {
                border-color: var(--shade-2);
            }

            .time-log-events {
                position: relative;
                margin-top: -0.3em;
            }

            .time-log-events::after {
                content: "";
                display: block;
                position: absolute;
                width: 1px;
                height: 100%;
                left: 1em;
                top: 0;
                border-left: dashed 1px;
                opacity: 0.5;
            }

            .time-log-events ptc-ui-icon {
                background: var(--color-bg);
                position: relative;
                z-index: 1;
                border-radius: 100%;
                width: 1.3em;
                height: 1.3em;
                line-height: 1.3em;
            }
        `,
    ];

    render() {
        if (!this.entry) {
            return html``;
        }

        const contract = this._employee?.getContractForDate(this.entry.date);
        const salary = contract && getSalaryOrDefault(this.entry.positionId, contract.salaries);
        const showRevenue = salary && salary.commission;
        const timeSettings = app.getTimeSettings({ timeEntry: this.entry });
        const positions = this.positions || this._employee?.positions || [];
        const isPast = this.entry.date < toDateString(new Date());
        const canEditFinal =
            app.hasPermission("manage.employees.time") &&
            this._employee &&
            app.hasPermissionForEmployee(this._employee);

        return html`
            <form
                @submit=${(e: Event) => e.preventDefault()}
                @input=${() => this._updateEntry()}
                @change=${() => this._commitChanges()}
            >
                <div class="vertical layout" hidden>
                    <label><ptc-ui-icon name="icon.object.date"></ptc-ui-icon>Datum</label>
                    <ptc-date-input
                        name="date"
                        required
                        @change=${(e: Event) => this._validateDate(e)}
                    ></ptc-date-input>
                </div>

                <div class="vertical layout" ?hidden=${this.entry.type !== TimeEntryType.Work}>
                    <div class="horizontal spacing center-aligning layout">
                        <label ?disabled=${this.readonly} class="stretch"
                            ><ptc-ui-icon name="icon.object.employee"></ptc-ui-icon>Position
                        </label>
                        <label class="smaller orange colored-text" ?hidden=${this.entry.position?.active}
                            ><ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>Archivierte Position</label
                        >
                    </div>
                    <select name="position" required ?disabled=${this.readonly}>
                        ${positions.map((position) => html` <option value=${position.id}>${position.name}</option> `)}
                    </select>
                </div>

                <div ?hidden=${this.entry.type !== TimeEntryType.Work}>
                    <label class="padded"> <ptc-ui-icon name="icon.planning.schedule"></ptc-ui-icon> Geplant </label>
                    <div class="horizontal center-aligning layout larger">
                        <ptc-ui-time-input
                            compact
                            class="stretch"
                            name="startPlanned"
                            ?disabled=${this.readonly || isPast || !!this.entry.startFinal}
                        ></ptc-ui-time-input>
                        <div class="dash" ?disabled=${this._editMode === "final" || this.readonly}>-</div>
                        <ptc-ui-time-input
                            compact
                            class="stretch"
                            name="endPlanned"
                            ?disabled=${this.readonly || isPast || !!this.entry.endFinal}
                        ></ptc-ui-time-input>
                        <ptc-ui-time-input
                            compact
                            class="stretch break"
                            name="breakPlanned"
                            icon="icon.object.break"
                            .hourDigits=${1}
                            ?disabled=${![
                                BreakMode.Planned,
                                BreakMode.PlannedPlusManual,
                                BreakMode.PlannedOrManual,
                            ].includes(timeSettings.breakMode) ||
                            this.readonly ||
                            isPast ||
                            !!this.entry.endFinal}
                        ></ptc-ui-time-input>
                    </div>
                </div>

                <div ?hidden=${this.entry.type !== TimeEntryType.Work || this.hideLogged}>
                    <label class="padded" disabled
                        ><ptc-ui-icon name="icon.feature.time-tracking"></ptc-ui-icon> Gestempelt</label
                    >
                    <div class="horizontal center-aligning layout larger">
                        <ptc-ui-time-input compact class="stretch" name="startLogged" disabled></ptc-ui-time-input>
                        <div class="dash" disabled>-</div>
                        <ptc-ui-time-input compact class="stretch" name="endLogged" disabled></ptc-ui-time-input>
                        <ptc-ui-time-input
                            compact
                            class="stretch break"
                            name="breakLogged"
                            icon="icon.object.break"
                            .hourDigits=${1}
                            disabled
                        ></ptc-ui-time-input>
                    </div>
                </div>

                <div ?hidden=${this.entry.type !== TimeEntryType.Work}>
                    <label class="padded"> <ptc-ui-icon name="icon.status.success"></ptc-ui-icon> Erfasst </label>
                    <div class="horizontal center-aligning layout larger">
                        <ptc-ui-time-input
                            compact
                            class="stretch"
                            name="startFinal"
                            ?disabled=${this._editMode !== "final" || this.readonly || !canEditFinal}
                        ></ptc-ui-time-input>
                        <div class="dash" ?disabled=${this._editMode !== "final" || this.readonly}>-</div>
                        <ptc-ui-time-input
                            compact
                            class="stretch"
                            name="endFinal"
                            ?disabled=${this._editMode !== "final" || this.readonly || !canEditFinal}
                        ></ptc-ui-time-input>
                        <ptc-ui-time-input
                            compact
                            class="stretch break"
                            name="break"
                            icon="icon.object.break"
                            .hourDigits=${1}
                            ?disabled=${this._editMode !== "final" || this.readonly || !canEditFinal}
                        ></ptc-ui-time-input>
                    </div>
                </div>

                <div
                    class="horizontal evenly stretching spacing layout"
                    ?hidden=${this.entry.type !== TimeEntryType.Work || this.hideMealsAndRevenue}
                >
                    <div class="vertical layout">
                        <label ?disabled=${this.readonly}
                            ><ptc-ui-icon name="icon.object.meal"></ptc-ui-icon>Essen</label
                        >
                        <ptc-meals-input
                            ?disabled=${this.readonly}
                            @change=${async () => {
                                await this._updateEntry();
                                void this._commitChanges();
                            }}
                        ></ptc-meals-input>
                    </div>

                    <div class="vertical layout" style="flex: 2;" ?disabled=${!showRevenue}>
                        <label ?disabled=${this.readonly}
                            ><ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>Umsatz</label
                        >
                        <div class="right icon input">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                name="revenue"
                                required
                                ?disabled=${this.readonly}
                            />
                            <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>
                        </div>
                    </div>
                </div>

                <div class="vertical layout">
                    <label
                        ><ptc-ui-icon name="icon.object.comment" ?disabled=${this.readonly}></ptc-ui-icon
                        >Bemerkungen</label
                    >
                    <ptc-textarea
                        name="comment"
                        @keydown=${(e: KeyboardEvent) => e.key !== "Escape" && e.stopPropagation()}
                        ?disabled=${this.readonly}
                    ></ptc-textarea>
                </div>

                <div class="vertical layout" ?hidden=${timeSettings === DRAFT_TIME_SETTINGS}>
                    <label class="center-aligning horizontal layout">
                        <div class="stretch"><ptc-ui-icon name="icon.object.rules"></ptc-ui-icon>Regeln</div>
                        <div
                            class="smaller link ellipsis subtle"
                            style="max-width: 10em;"
                            @click=${() => router.go("settings/time", { sid: timeSettings.id.toString() })}
                            ?disabled=${!app.hasPermission("manage.settings.time")}
                        >
                            ${timeSettings.name}
                        </div>
                    </label>
                    <div class="tiny semibold horizontally-padded horizontal spacing wrapping layout time-settings">
                        <div class="half-padded box centering spacing horizontal layout" title="Pausenmodus">
                            <ptc-ui-icon name="icon.object.break"></ptc-ui-icon>
                            <div>${breakModeLabel(timeSettings.breakMode)}</div>
                        </div>

                        <div class="padded box centering horizontal layout" title="Mitarbeiteressen">
                            <ptc-ui-icon name="icon.object.meal"></ptc-ui-icon>
                            <div>
                                ${timeSettings.mealsMode === MealsMode.Auto
                                    ? "Auto"
                                    : timeSettings.mealsMode === MealsMode.Employee
                                      ? "Durch MA"
                                      : "Manuell"}
                            </div>
                        </div>

                        <div
                            class="half-padded box centering horizontal layout ${timeSettings.trackingEnabled
                                ? ""
                                : "strike-through"}"
                            title="Zeiterfassung per Stempeluhr"
                        >
                            <ptc-ui-icon name="icon.feature.time-tracking" class="larger"></ptc-ui-icon>
                        </div>

                        ${timeSettings.trackingEnabled
                            ? html`
                                  <div
                                      class="half-padded box centering horizontal layout ${timeSettings.trackingViaAppEnabled
                                          ? ""
                                          : "strike-through"}"
                                      title="Zeiterfassung per Mitarbeiter-App"
                                  >
                                      <ptc-ui-icon name="icon.object.mobile-app" class="larger"></ptc-ui-icon>
                                  </div>

                                  <div
                                      ?hidden=${!timeSettings.trackingViaAppEnabled}
                                      class="half-padded box centering horizontal layout ${timeSettings.trackingViaAppRequiresToken
                                          ? ""
                                          : "strike-through"}"
                                      title="Stempeln per MA-App erfordert QR-Code"
                                  >
                                      <ptc-ui-icon name="icon.action.scan-code" class="larger"></ptc-ui-icon>
                                  </div>
                              `
                            : ""}
                    </div>
                </div>

                ${this.timeLogEvents?.length
                    ? html`
                          <div class="vertical layout">
                              <label
                                  ><ptc-ui-icon name="icon.object.time-log"></ptc-ui-icon>
                                  Zeiterfassungsprotokoll</label
                              >
                              <div class="time-log-events">
                                  ${this.timeLogEvents
                                      .sort((a, b) => a.time.getTime() - b.time.getTime())
                                      .map(
                                          (event) => html`
                                              <div
                                                  class="smaller center-aligning spacing horizontal layout time-log-event margined"
                                                  style="padding-left: 0.18em; margin-bottom: 0;"
                                                  ${event.image ||
                                                  event.comment ||
                                                  event.location ||
                                                  event.rejectedReason
                                                      ? popover(
                                                            html`
                                                                <ptc-time-log-event-details
                                                                    .event=${event}
                                                                ></ptc-time-log-event-details>
                                                            `,
                                                            {
                                                                trigger: "hover",
                                                                preferAlignment: ["left", "left-bottom", "left-top"],
                                                            }
                                                        )
                                                      : nothing}
                                              >
                                                  <ptc-ui-icon
                                                      name=${activeIcon(timeLogActionIcon(event.action), true)}
                                                  ></ptc-ui-icon>

                                                  <div
                                                      class="stretch ellipsis ${event.status === "rejected"
                                                          ? "red colored-text line-through"
                                                          : ""}"
                                                  >
                                                      ${formatDateShort(event.time, true)}

                                                      <strong>${toTimeString(event.time)}</strong>

                                                      ${timeLogActionLabel(event.action)}
                                                  </div>

                                                  <div>
                                                      ${event.status === "rejected"
                                                          ? html`<ptc-ui-icon
                                                                name="icon.status.warning"
                                                                class="red colored-text"
                                                            ></ptc-ui-icon>`
                                                          : ""}
                                                      ${event.location
                                                          ? html`<ptc-ui-icon
                                                                name="icon.object.location"
                                                            ></ptc-ui-icon>`
                                                          : ""}
                                                      ${event.image
                                                          ? html`<ptc-ui-icon
                                                                name="icon.object.proof-photo"
                                                            ></ptc-ui-icon>`
                                                          : ""}
                                                      ${event.comment
                                                          ? html`<ptc-ui-icon name="icon.object.comment"></ptc-ui-icon>`
                                                          : ""}
                                                      ${event.source === "employee_app"
                                                          ? html`<ptc-ui-icon
                                                                name="icon.object.mobile-app"
                                                                title="Über Mitarbeiter-App erfasst"
                                                            ></ptc-ui-icon>`
                                                          : ""}
                                                  </div>
                                              </div>
                                          `
                                      )}
                              </div>
                          </div>
                      `
                    : ""}
            </form>
        `;
    }
}
